/**
 * The interface between the dashboard's Maandrapport page and its renderer, and the shapes the backend answers
 * (schema "maandrapport/2"): the snapshot's sections, the view of a date range and the details behind a figure.
 * Amounts are whole euros without VAT, shares fractions, dates ISO in Belgium.
 */

import type {
  BezettingSectionLoader as SectionLoader,
  BezettingSnapshotRef as SnapshotRef,
  RecomputeHook,
} from "../bezetting/contract";

export type { RecomputeHook, SectionLoader, SnapshotRef };

export const MAANDRAPPORT_TABS = [
  "overzicht",
  "omzet",
  "klanten",
  "betalingen",
  "ongefactureerd",
  "prijzen",
] as const;
export type MaandrapportTab = (typeof MAANDRAPPORT_TABS)[number];
export const MAANDRAPPORT_DEFAULT_TAB: MaandrapportTab = "overzicht";
export const MAANDRAPPORT_PATH = "/apps/maandrapport";
/**
 * The sections' schema this renderer reads. A snapshot of another schema (the first version's, before a release
 * recomputes it) holds none of the facts the ranges are computed from: the page asks for Herbereken instead.
 */
export const MAANDRAPPORT_SCHEMA = "maandrapport/2";
/** A client's page: `/apps/maandrapport/klanten/<partner id>`. */
export const CLIENT_SEGMENT = "klanten";
/** The query key of a quick choice ("30-dagen"), and of the months of the first version ("2026-10"). */
export const PERIOD_PARAM = "periode";
/** The query keys of a range of one's own. */
export const FROM_PARAM = "van";
export const TO_PARAM = "tot";
/** The open detail: "<kind>:<key>". */
export const DETAIL_PARAM = "detail";
/** A returning or new clients' list of the comparison: "vorig". */
export const AT_PARAM = "bij";
/**
 * Where a client's page was opened from, so its back link returns there: a tab ("betalingen"), and the popup that was
 * open over it ("ageing:d31_60").
 */
export const BACK_PARAM = "terug";
export const BACK_DETAIL_PARAM = "terugdetail";
export const BACK_AT_PARAM = "terugbij";

/** A compact table: `{cols, rows}`. */
export interface Table {
  readonly cols: readonly string[];
  readonly rows: readonly (readonly unknown[])[];
}

/** Client id (as a string) to name. */
export type Names = Readonly<Record<string, string>>;

export interface DateRangeShape {
  readonly from: string;
  readonly to: string;
}

export interface MetaSection {
  readonly section: "meta";
  readonly schema: string;
  readonly as_of: string;
  readonly odoo_read_at: string;
  readonly built_at: string;
  /** The first day a range may start: a full year of history lies before it. */
  readonly range_min?: string;
  /** The last day a range may end: the as-of day. */
  readonly range_max?: string;
  readonly max_days?: number;
  readonly default_range?: DateRangeShape;
}

/**
 * The activities the app shows. Revenue lines Odoo files under none of them are a data issue to be fixed in Odoo: the
 * backend leaves them out of every total, so each total is the sum of these.
 */
export const ACTIVITIES = [
  "kraanverhuur",
  "transport",
  "kraanverkoop",
  "onderdelen",
  "service",
] as const;
export type Activity = (typeof ACTIVITIES)[number];

export const REGIONS = ["vlaanderen", "wallonie", "brussel", "buitenland", "onbekend"] as const;
export type Region = (typeof REGIONS)[number];

export interface OmzetMaandenSection {
  readonly section: "omzet_maanden";
  readonly months: readonly string[];
  readonly partial_month: string | null;
  readonly activities: Readonly<Record<Activity, readonly number[]>>;
  readonly regions: Readonly<Record<Region, readonly number[]>>;
}

/**
 * Per due month from January 2024 to the month in progress: the amount due, split by its status on the snapshot day
 * (paid on time is the rest: due less the three parts), and the on-time share of what had fallen due.
 */
export interface BetalingenMaandenSection {
  readonly section: "betalingen_maanden";
  readonly months: readonly string[];
  readonly on_time_share: readonly (number | null)[];
  readonly due_amount: readonly number[];
  readonly paid_late: readonly number[];
  readonly open_overdue: readonly number[];
  readonly open_not_due: readonly number[];
  readonly partial_month: string | null;
}

/** Every client with revenue or a confirmed order since 2024, for the client search: id, name, revenue, last_order. */
export interface KlantenIndexSection extends Table {
  readonly section: string;
}

export interface KlantenIndexRow {
  readonly id: number;
  /** The backend names every client ("Klant <id>" without one in Odoo); null only from an older snapshot. */
  readonly name: string | null;
  readonly revenue: number;
  readonly last_order: string | null;
}

export interface ThisLast {
  readonly this: number;
  readonly last: number;
}

export interface OmzetPeriod {
  readonly activities: readonly ({ readonly key: Activity } & ThisLast)[];
  readonly total: ThisLast;
  readonly regions: readonly ({ readonly key: Region } & ThisLast)[];
  /** activity, region, this, last */
  readonly grid: Table;
  /**
   * The revenue per day, whole euros: from the range's first day to its last (`this`), and over the comparison
   * (`last`), aligned by day index. Only the view of a range carries it.
   */
  readonly daily: OmzetDaily;
}

export interface OmzetDaily {
  readonly this: readonly number[];
  readonly last: readonly number[];
}

export interface Returning {
  /** Clients with a confirmed order in the 12 months before the range. */
  readonly base: number;
  /** Of them, the clients with a confirmed order again in the range. */
  readonly again: number;
  /** The base window's revenue of the base clients not back. */
  readonly away_revenue: number;
  readonly base_revenue: number;
  /** The base window: the 12 months before the range (the previous calendar year for a range from 1 January). */
  readonly base_from: string;
  readonly base_to: string;
}

export interface ClientChange {
  readonly id: number;
  readonly this: number;
  readonly last: number;
}

export interface Concentration {
  /** "alles" or an activity */
  readonly key: string;
  readonly top10: number;
  readonly total: number;
  readonly clients: number;
  readonly clients_for_80: number;
  readonly last_top10_share: number | null;
}

/** A count for the range and its comparison, null where the order history is too short to count it. */
export interface MaybeThisLast {
  readonly this: number | null;
  readonly last: number | null;
}

export interface KlantenPeriod {
  /** null where the order history is too short for the 12 months before. */
  readonly returning: { readonly this: Returning | null; readonly last: Returning | null };
  readonly active: MaybeThisLast;
  /** Clients whose first confirmed order ever falls in the range: null without a year of order history before it. */
  readonly new: MaybeThisLast;
  readonly risers: readonly ClientChange[];
  readonly fallers: readonly ClientChange[];
  readonly concentration: readonly Concentration[];
}

export const BANDS = [
  "niet_vervallen",
  "d1_30",
  "d31_60",
  "d61_90",
  "d91_180",
  "d181_365",
  "meer_365",
] as const;
export type Band = (typeof BANDS)[number];

export interface Ageing {
  readonly date: string;
  readonly open: number;
  readonly overdue: number;
  readonly invoices: number;
  /** Per band (BANDS); a band missing counts as 0. */
  readonly bands: Readonly<Record<string, number>>;
}

export interface Speed {
  readonly due_amount: number;
  readonly on_time_share: number | null;
  readonly weighted_days: number | null;
  readonly paid_invoices: number;
}

export interface BetalingenPeriod {
  /** What stood open at the range's end. */
  readonly ageing: Ageing;
  readonly speed: { readonly this: Speed; readonly last: Speed | null };
  /** The clients who paid invoices in the range, and what stood overdue with them at its end. */
  readonly payers: {
    readonly clients: number;
    readonly slow: number;
    readonly slow_overdue: number;
    readonly on_time: number;
    readonly overdue_total: number;
    /**
     * id, delay, paid, overdue, invoices. delay is null for a client listed for its overdue amount that paid no
     * invoice in the range.
     */
    readonly dots: Table;
  };
}

export interface TransportClass {
  readonly key: "BK" | "TK";
  readonly count: number;
  readonly weighted: number | null;
  readonly within_10: number | null;
  readonly under_80: number;
  readonly shortfall: number;
  readonly last_weighted: number | null;
}

/** The rentals of a range against the list price. */
export interface Rentals {
  readonly weighted: number | null;
  readonly last_weighted: number | null;
  readonly rent: number;
  readonly missed: number;
  readonly above: number;
  readonly count: number;
  /** type, class, count, weighted, rent, missed */
  readonly types: Table;
  /** type, ratio, rent */
  readonly dots: Table;
  /**
   * Per crane class, over the types the chart shows: their count, their rentals and the weighted price against the
   * list price, on the unrounded prices. Missing from a backend of before: the class's header then counts the rows.
   */
  readonly classes?: readonly RentalClass[];
}

export interface RentalClass {
  readonly key: string;
  readonly types: number;
  readonly count: number;
  readonly weighted: number | null;
}

/** The transports of a range against the tariff. */
export interface Transports {
  readonly classes: readonly TransportClass[];
  /** class, ratio, invoiced */
  readonly dots: Table;
}

export const FINDINGS = ["geleverd", "verhuur", "demontage"] as const;
export type Finding = (typeof FINDINGS)[number];
export const UNBILLED_CLASSES = [
  "zonder_spoor",
  "onduidelijk",
  "verklaard",
  "eigen_vloot",
] as const;
export type UnbilledClass = (typeof UNBILLED_CLASSES)[number];

export interface UnbilledCell {
  readonly amount: number;
  readonly items: number;
}

/**
 * Every total is the backend's own, summed to the cent before it rounds: a finding's `amount` and `items`, and each
 * class over every finding (`classes`). A snapshot from before they were added lacks them, and the page then adds up
 * the rounded cells.
 */
export interface OngefactureerdSection {
  readonly section: "ongefactureerd";
  readonly as_of: string;
  readonly total: number;
  readonly items: number;
  readonly classes?: Readonly<Record<UnbilledClass, UnbilledCell>>;
  readonly findings: readonly {
    readonly key: Finding;
    readonly amount?: number;
    readonly items?: number;
    readonly classes: Readonly<Record<UnbilledClass, UnbilledCell>>;
  }[];
  /** order, client_id, finding, class, amount, date */
  readonly largest: Table;
  readonly names: Names;
}

// ---------------------------------------------------------------- the view of a range

export interface ViewRange extends DateRangeShape {
  readonly compare_from: string;
  readonly compare_to: string;
  readonly days: number;
}

/** `GET /snapshots/{id}/view?from&to`: every chapter for one range. */
export interface MaandrapportView {
  readonly range: ViewRange;
  readonly omzet: OmzetPeriod;
  readonly klanten: KlantenPeriod;
  readonly betalingen: BetalingenPeriod;
  readonly huur: { readonly rentals: Rentals };
  readonly transport: { readonly transports: Transports };
  readonly names: Names;
}

// ---------------------------------------------------------------- details

export const DETAIL_KINDS = [
  "clients",
  "due_invoices",
  "activity",
  "region",
  "ageing",
  "due_month",
  "concentration",
  "rentals",
  "transports",
  "unbilled",
  "returning",
  "new_clients",
  "invoice",
  "client",
] as const;
export type DetailKind = (typeof DETAIL_KINDS)[number];

/** The key of a detail that has none to choose (`clients`, `due_invoices`): the whole range. */
export const NO_KEY = "";

/** An Odoo record a detail lists: an invoice or an order, with its link when the backend gives one. */
export interface OdooLinked {
  readonly odoo_url?: string | null;
}

interface DetailBase {
  readonly kind: DetailKind;
  readonly key: string;
  readonly range: ViewRange;
  readonly names: Names;
}

export interface KeyedThisLast extends ThisLast {
  readonly key: string;
}

export interface ActivityDetail extends DetailBase, ThisLast {
  readonly regions: readonly KeyedThisLast[];
  /** id, this, last */
  readonly clients: Table;
}

export interface RegionDetail extends DetailBase, ThisLast {
  readonly activities: readonly KeyedThisLast[];
  /** id, this, last */
  readonly clients: Table;
}

export interface OpenInvoice extends OdooLinked {
  readonly id: number;
  readonly name: string | null;
  readonly invoice_date: string;
  readonly due: string;
  readonly days: number;
  readonly open: number;
}

/** An open document: an invoice, or a credit note (its open amount below zero). */
export interface OpenDocument extends OpenInvoice {
  readonly kind: "invoice" | "credit_note";
}

export interface AgeingClient {
  readonly id: number;
  /** Its open invoices less its open credit notes, excl. VAT. */
  readonly open: number;
  /** How many of its documents are invoices, and how many credit notes. */
  readonly invoices: number;
  readonly credit_notes: number;
  readonly documents: readonly OpenDocument[];
}

export interface AgeingDetail extends DetailBase {
  readonly date: string;
  readonly total: number;
  /** The open invoices, credit notes not counted. */
  readonly invoices: number;
  /** Largest open amount first; the clients whose credit notes outweigh their invoices (0 or less) last. */
  readonly clients: readonly AgeingClient[];
}

/** An invoice due, by its status on the snapshot day. */
export const DUE_STATUSES = ["on_time", "paid_late", "open_overdue", "open_not_due"] as const;
export type DueStatus = (typeof DUE_STATUSES)[number];

export interface DueInvoice extends OdooLinked {
  readonly id: number;
  readonly name: string | null;
  readonly due: string;
  readonly amount: number;
  /** Open on the snapshot day, excl. VAT, at most what it owed: what its status part counts for one still open. */
  readonly open: number;
  readonly paid_on: string | null;
  /** Days after the due date it was paid; for an open one, the days since it fell due by the snapshot day. */
  readonly days: number | null;
  readonly status: DueStatus;
}

/** An amount due split by status: paid on time, paid late, past due and open, not yet due. */
export interface DueSplit {
  readonly due: number;
  readonly on_time: number;
  readonly paid_late: number;
  readonly open_overdue: number;
  readonly open_not_due: number;
}

export interface DueMonthDetail extends DetailBase {
  readonly month: string;
  readonly due_amount: number;
  readonly on_time: number;
  readonly paid_late: number;
  readonly open_overdue: number;
  readonly open_not_due: number;
  /** Of what had fallen due, the share paid on time. */
  readonly on_time_share: number | null;
  readonly clients: readonly (DueSplit & {
    readonly id: number;
    readonly invoices: readonly DueInvoice[];
  })[];
}

export interface ConcentrationDetail extends DetailBase {
  readonly total: number;
  readonly clients: readonly {
    readonly id: number;
    readonly amount: number;
    readonly share: number | null;
    readonly cumulative: number | null;
  }[];
}

export interface RentalItem extends OdooLinked {
  readonly order: string | null;
  readonly client_id: number | null;
  readonly price: number;
  readonly list: number;
  readonly ratio: number | null;
  readonly rent: number;
  readonly months: number | null;
  /** On a client's page: the crane type. */
  readonly type?: string;
}

/** The rentals' figures, computed by the backend on the unrounded prices, so they match the Prijzen page. */
export interface RentalsSummary {
  /** The price against the list price, weighted by the invoiced months; null without a list price. */
  readonly weighted: number | null;
  readonly rent: number;
  /** What the rentals under the list price would have brought in more at it. */
  readonly missed: number;
  readonly count: number;
  /** The rentals below the list price, by the backend's own rule; missing from an older backend. */
  readonly below?: number;
}

export interface RentalsDetail extends DetailBase {
  readonly type: string;
  readonly class: string;
  readonly summary: RentalsSummary;
  readonly rentals: readonly RentalItem[];
}

export interface TransportItem extends OdooLinked {
  readonly order: string | null;
  readonly client_id: number | null;
  readonly date: string;
  readonly ratio: number | null;
  readonly invoiced: number;
  readonly expected: number;
  readonly shortfall: number;
  readonly class?: string;
}

/** The transports' figures, computed by the backend on the unrounded amounts, so they match the Prijzen page. */
export interface TransportsSummary {
  /** The invoiced total against the tariff's; null without a tariff. */
  readonly weighted: number | null;
  /** The share of the transports within 10% of the tariff. */
  readonly within_10: number | null;
  /** The transports under 80% of the tariff, and how far under it they were together. */
  readonly under_80: number;
  readonly shortfall: number;
  readonly count: number;
}

export interface TransportsDetail extends DetailBase {
  readonly class: string;
  readonly summary: TransportsSummary;
  readonly transports: readonly TransportItem[];
}

export interface UnbilledItem extends OdooLinked {
  readonly order: string | null;
  readonly client_id: number | null;
  readonly finding: Finding;
  readonly class: UnbilledClass;
  readonly amount: number;
  readonly date: string | null;
  readonly note: string | null;
}

export interface UnbilledDetail extends DetailBase {
  /** Computed on the unrounded items, as the ongefactureerd section is. */
  readonly total: number;
  readonly count: number;
  /** Per finding the detail covers (every finding for `alle:<class>`). */
  readonly findings: readonly {
    readonly key: string;
    readonly total: number;
    readonly count: number;
  }[];
  readonly items: readonly UnbilledItem[];
}

/** A client of a returning clients' list. */
export interface ReturningClient {
  readonly id: number;
  /** Its revenue in the 12 months before the range. */
  readonly base_revenue: number;
  /** Its revenue in the range. */
  readonly revenue: number;
  /** Its last confirmed order up to the range's end. */
  readonly last_order: string | null;
}

/** The clients of the 12 months before the range not back in it (`away`), or back (`back`). */
export interface ReturningDetail extends DetailBase {
  readonly at: "this" | "last";
  readonly base_from: string;
  readonly base_to: string;
  readonly base: number;
  readonly count: number;
  readonly base_revenue: number;
  readonly revenue: number;
  readonly clients: readonly ReturningClient[];
}

/** The clients whose first confirmed order ever falls in the range. */
export interface NewClientsDetail extends DetailBase {
  readonly at: "this" | "last";
  readonly count: number;
  readonly revenue: number;
  readonly clients: readonly {
    readonly id: number;
    readonly first_order: string | null;
    readonly orders: number;
    readonly revenue: number;
  }[];
}

/** An invoice's status on the snapshot day. */
export type InvoiceStatus = "paid" | "settled" | "overdue" | "open";

export interface InvoiceLine {
  readonly product_code: string | null;
  readonly product_name: string | null;
  readonly quantity: number | null;
  readonly unit_price: number | null;
  /** In percent. */
  readonly discount: number;
  /** Excl. VAT, as on the document. */
  readonly amount: number;
  readonly activity: string | null;
}

export interface InvoiceReconciliation {
  readonly date: string | null;
  /** Incl. VAT. */
  readonly amount: number;
  readonly kind: "payment" | "credit_note" | "invoice";
  readonly document: ({ readonly id: number; readonly name: string | null } & OdooLinked) | null;
}

/** An invoice due in the range, by its state on the snapshot day: paid, open past its due date, or not yet due. */
export const DUE_INVOICE_STATUSES = ["paid", "open_overdue", "open_not_due"] as const;
export type DueInvoiceStatus = (typeof DUE_INVOICE_STATUSES)[number];

export interface DueInvoiceRow {
  readonly id: number;
  readonly client_id: number;
  /** Excl. VAT. */
  readonly amount: number;
  /**
   * Days after the due date: paid then, or past it on the snapshot day; for one not yet due, the days until it falls
   * due, below zero.
   */
  readonly days: number;
  readonly status: DueInvoiceStatus;
}

/** Every invoice due in the range: the late payers' chart per invoice, the largest first. */
export interface DueInvoicesDetail extends DetailBase {
  readonly count: number;
  readonly amount: number;
  /** The count and amount per status. */
  readonly statuses: readonly {
    readonly key: string;
    readonly count: number;
    readonly amount: number;
  }[];
  /** id, client_id, amount, days, status */
  readonly invoices: Table;
}

/** One client of the Klanten tab's list: its revenue in the range and its comparison, and what it did in the range. */
export interface ClientsRow {
  readonly id: number;
  readonly city: string | null;
  readonly this: number;
  readonly last: number;
  /** Of the range's revenue; null without revenue, or when the range's total is not above zero. */
  readonly share: number | null;
  /** Its largest activity in the range, null without revenue in it. */
  readonly activity: string | null;
  /** Its confirmed orders in the range. */
  readonly orders: number;
  readonly last_order: string | null;
  /** Overdue at the range's end, excl. VAT. */
  readonly overdue: number;
}

/** Every client with revenue or a confirmed order in the range or its comparison, largest revenue in the range first. */
export interface ClientsDetail extends DetailBase {
  /** id, city, this, last, share, activity, orders, last_order, overdue */
  readonly clients: Table;
  /** The revenue of the range and of its comparison. */
  readonly total: ThisLast;
  readonly count: number;
  /** The new clients of the range (first order ever in it), null without a year of order history before it. */
  readonly new: { readonly count: number; readonly revenue: number } | null;
}

/** One invoice or credit note as it stands on the snapshot day, whatever the range. */
export interface InvoiceDetail extends DetailBase, OdooLinked {
  readonly id: number;
  readonly name: string | null;
  readonly document: "invoice" | "credit_note";
  readonly client_id: number;
  readonly invoice_date: string | null;
  readonly due: string | null;
  /** As on the document: a credit note's are positive. */
  readonly untaxed: number;
  readonly total: number;
  /** What is open, excl. VAT, and incl. VAT. */
  readonly open: number;
  readonly open_total: number;
  readonly status: InvoiceStatus;
  readonly paid_on: string | null;
  /** Days after the due date it was paid, or since it fell due for one still open; null when settled otherwise. */
  readonly days: number | null;
  readonly lines: readonly InvoiceLine[];
  readonly reconciliations: readonly InvoiceReconciliation[];
  readonly orders: readonly ({ readonly id: number; readonly name: string | null } & OdooLinked)[];
}

export interface ClientOrder extends OdooLinked {
  readonly id: number;
  readonly name: string | null;
  readonly date: string;
  readonly type: string | null;
  readonly amount?: number | null;
}

export interface PaidInvoice extends OdooLinked {
  readonly id: number;
  readonly name: string | null;
  readonly due: string;
  readonly paid_on: string | null;
  readonly days: number | null;
  readonly amount: number;
}

/**
 * A client's company data from Odoo, for its page: only what Odoo holds for it, a field Odoo lacks left out. No
 * personal phone numbers or email addresses.
 */
export interface ClientInfo {
  readonly street?: string | null;
  readonly zip?: string | null;
  readonly city?: string | null;
  readonly country?: string | null;
  readonly vat?: string | null;
  /** The payment terms by their name in Odoo: "30 dagen". */
  readonly payment_terms?: string | null;
  readonly salesperson?: string | null;
  readonly tags?: readonly string[] | null;
  /** The day of its first invoice or order in Odoo's history. */
  readonly since?: string | null;
  /** Excl. VAT; absent or 0 when none is set. */
  readonly credit_limit?: number | null;
}

export interface ClientDetail extends DetailBase, OdooLinked {
  readonly id: number;
  readonly name: string | null;
  readonly city: string | null;
  /** Missing from a snapshot of before. */
  readonly details?: ClientInfo | null;
  readonly totals: ThisLast;
  readonly activities: readonly KeyedThisLast[];
  readonly months: {
    readonly months: readonly string[];
    readonly activities: Readonly<Record<string, readonly number[] | undefined>>;
  };
  readonly orders: {
    readonly this: number;
    readonly last: number;
    readonly first: string | null;
    readonly last_order: string | null;
    readonly list: readonly ClientOrder[];
  };
  readonly open: {
    readonly date: string;
    readonly open: number;
    readonly overdue: number;
    /** Its open invoices and credit notes. */
    readonly invoices: readonly OpenDocument[];
  };
  readonly payments: {
    readonly weighted_delay: number | null;
    readonly paid_invoices: number;
    readonly invoices: readonly PaidInvoice[];
  };
  readonly rentals: readonly RentalItem[];
  /** The rentals' figures on the unrounded prices, as the Prijzen page computes them. */
  readonly rentals_summary: RentalsSummary;
  readonly transports: readonly TransportItem[];
  readonly transports_summary: TransportsSummary;
  readonly unbilled: readonly UnbilledItem[];
  readonly returning: { readonly in_base: boolean; readonly back: boolean };
}

export interface DetailRequest {
  readonly kind: DetailKind;
  readonly key: string;
  readonly from: string;
  readonly to: string;
  /** A returning or new clients' list of the range (this, the default) or of the comparison (last). */
  readonly at?: "this" | "last";
}

/** The view of one range, fetched once per snapshot and range. */
export type ViewLoader = (range: DateRangeShape) => Promise<MaandrapportView>;
/** One detail, fetched once per snapshot, kind, key, range and moment. */
export type DetailLoader = (request: DetailRequest) => Promise<unknown>;

/** Bezettingsgraad's own key figure for the Overzicht's tile: the share of crane-days on rent this year so far. */
export interface BezettingFigure {
  readonly rate: number | null;
  readonly lastYear: number | null;
  /** The last day it counts. */
  readonly to: string | null;
}

/** This person may open Bezettingsgraad; `figure` is null while it loads or without a snapshot. */
export interface BezettingTile {
  readonly figure: BezettingFigure | null;
}

/** What the page tells the renderer, and what it calls back. */
export interface MaandrapportDeps {
  readonly tab: MaandrapportTab;
  /** The client whose page is open (under Klanten), else null. */
  readonly client: number | null;
  readonly snapshot: SnapshotRef;
  readonly sections: SectionLoader;
  readonly view: ViewLoader;
  readonly detail: DetailLoader;
  readonly recompute: RecomputeHook;
  /** Null when this person may not open Bezettingsgraad: Overzicht then has no tile for it. */
  readonly bezetting: BezettingTile | null;
  /** The app URL's query string, read and written through the router. */
  readonly query: {
    read(): URLSearchParams;
    /** In place of the current entry. */
    write(params: URLSearchParams): void;
    /** As a new history entry, so Back returns (an opened detail). */
    push(params: URLSearchParams): void;
  };
  /** Another tab (or a client's page under Klanten) with its query, as a new history entry. */
  navigate(tab: MaandrapportTab, params: URLSearchParams, client?: number | null): void;
  /** The history entry before this one. */
  back(): void;
  /** A dashboard page outside the app (Bezettingsgraad). */
  open(path: string): void;
  onError(error: unknown): void;
  /**
   * The snapshot is of an older schema (its meta says so): the host shows Herbereken in place of the page. A view or
   * detail answering 409 `snapshot_outdated` reaches the host through its loaders instead.
   */
  onOutdated?(): void;
}

/** What changed outside the renderer; `search` is the query string without "?". */
export type MaandrapportUpdate = Partial<
  Pick<
    MaandrapportDeps,
    "tab" | "client" | "snapshot" | "sections" | "view" | "detail" | "bezetting"
  >
> & { readonly search?: string };

export interface MaandrapportHandle {
  update(next: MaandrapportUpdate): void;
  /** Removes every element, listener, observer and timer the mount added. */
  destroy(): void;
}
